import { Payment } from '@klear/payment/payment'
import type { PaymentAccount } from '@klear/payment/payment-account'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Badge } from '@klear/ui/components/badge'
import { Separator } from '@klear/ui/components/separator'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@klear/ui/components/tooltip'
import { Alert, AlertDescription } from '@klear/ui/components/alert'
import { CreditCard, ArrowRightLeft, Building2, FileText, Info, CheckCircle, AlertCircle } from 'lucide-react'

interface PaymentPreviewProps {
  payment: Payment
  sourceAccount: PaymentAccount
  destinationAccount: PaymentAccount
}
export const PaymentPreview = ({ payment, sourceAccount, destinationAccount }: PaymentPreviewProps) => {
  const hasIncompleteAccountInfo =
    !sourceAccount.accountNumber ||
    !sourceAccount.routingNumber ||
    !destinationAccount.accountNumber ||
    !destinationAccount.routingNumber

  return (
    <TooltipProvider>
      <Card className="overflow-hidden">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2">Payment Preview</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1 space-y-4">
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4 text-muted-foreground" />
                <label className="text-sm font-medium text-muted-foreground">Payment Type</label>
              </div>
              <Badge variant="secondary" className="text-sm">
                {payment.paymentTypeLabel}
              </Badge>

              <div className="flex items-center gap-2">
                <ArrowRightLeft className="h-4 w-4 text-muted-foreground" />
                <label className="text-sm font-medium text-muted-foreground">Network</label>
              </div>
              <Badge variant="outline" className="text-sm">
                {payment.paymentNetworkLabel}
              </Badge>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <label className="text-sm font-medium text-muted-foreground">Account Information</label>
                {hasIncompleteAccountInfo && (
                  <Tooltip>
                    <TooltipTrigger>
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Some account information is missing</p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="relative">
                  <div className="absolute -top-2 left-3 bg-background px-2 text-xs font-medium text-muted-foreground">
                    From
                  </div>
                  <div className="border rounded-lg p-4 bg-muted/30">
                    <div className="space-y-2">
                      <p className="font-semibold text-sm">{payment.sourceAccountOrganizationName || 'Not selected'}</p>
                      <p className="text-sm text-muted-foreground">{sourceAccount.name}</p>
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <div className="flex justify-between">
                          <span>Account:</span>
                          <span className="font-mono">
                            {sourceAccount.accountNumber
                              ? `****${sourceAccount.accountNumber.slice(-4)}`
                              : 'Not selected'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Routing:</span>
                          <span className="font-mono">{sourceAccount.routingNumber || 'Not selected'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative">
                  <div className="absolute -top-2 left-3 bg-background px-2 text-xs font-medium text-muted-foreground">
                    To
                  </div>
                  <div className="border rounded-lg p-4 bg-muted/30">
                    <div className="space-y-2">
                      <p className="font-semibold text-sm">{destinationAccount.organizationName || 'Not selected'}</p>
                      <p className="text-sm text-muted-foreground">{destinationAccount.name}</p>
                      <div className="space-y-1 text-xs text-muted-foreground">
                        <div className="flex justify-between">
                          <span>Account:</span>
                          <span className="font-mono">
                            {destinationAccount.accountNumber
                              ? `****${destinationAccount.accountNumber.slice(-4)}`
                              : 'Not selected'}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Routing:</span>
                          <span className="font-mono">{destinationAccount.routingNumber || 'Not selected'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {payment.internalNotes && (
            <>
              <Separator />
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <label className="text-sm font-medium text-muted-foreground">Internal Notes</label>
                </div>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm">{payment.internalNotes}</AlertDescription>
                </Alert>
              </div>
            </>
          )}

          {payment.notes && (
            <>
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <label className="text-sm font-medium text-muted-foreground">External Notes</label>
                </div>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm">{payment.notes}</AlertDescription>
                </Alert>
              </div>
            </>
          )}

          {payment.statementDescriptor && (
            <>
              <Separator />
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <label className="text-sm font-medium text-muted-foreground">Payment Reference</label>
                </div>
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-sm">{payment.statementDescriptor}</AlertDescription>
                </Alert>
              </div>
            </>
          )}

          <Separator />
          <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <span className="text-sm font-medium">Payment Ready</span>
            </div>
            <div className="text-right">
              <p className="text-xs text-muted-foreground">Amount</p>
              <p className="text-2xl font-semibold">{payment.amountFormatted}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  )
}
